import { useState } from 'react';
import { useLoaderData, useSearchParams, Link } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { 
  Activity, 
  Filter, 
  Calendar,
  FileText,
  Target,
  Send,
  Eye,
  Bookmark,
  ThumbsDown,
  Upload,
  Settings,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { requireUser } from '../../lib/session.server';
import { Card, Button, Badge } from '../../components/ui';
import type { User, ActivityEvent } from '../../types';

interface ActivityData {
  user: User;
  events: ActivityEvent[];
  total_count: number;
  page: number;
  has_more: boolean;
  filters: {
    type?: string;
    resource?: string;
    date_from?: string;
    date_to?: string;
  };
}

// Mock activity data
const mockEvents: ActivityEvent[] = [
  {
    id: '1',
    type: 'job_applied',
    timestamp: '2024-01-15T14:30:00Z',
    user_id: '1',
    resource_type: 'job',
    resource_id: 'job-123',
    resource_title: 'Senior Software Engineer',
    details: 'Applied to Senior Software Engineer position at TechCorp via direct application',
    metadata: {
      strategy: 'HIGH_COMP',
      source: 'discover_feed',
      company: 'TechCorp',
      location: 'Remote',
      application_method: 'direct'
    }
  },
  {
    id: '2',
    type: 'job_saved',
    timestamp: '2024-01-15T13:45:00Z',
    user_id: '1',
    resource_type: 'job',
    resource_id: 'job-124',
    resource_title: 'Frontend Lead',
    details: 'Saved Frontend Lead position at StartupCo to apply bucket',
    metadata: {
      strategy: 'EASY_WIN',
      source: 'matches_page',
      company: 'StartupCo',
      location: 'San Francisco, CA',
      bucket: 'to_apply'
    }
  },
  {
    id: '3',
    type: 'resume_uploaded',
    timestamp: '2024-01-15T10:15:00Z',
    user_id: '1',
    resource_type: 'resume',
    resource_id: 'resume-456',
    resource_title: 'Senior_Engineer_Resume_2024.pdf',
    details: 'Uploaded and processed Senior_Engineer_Resume_2024.pdf (1MB, 2 pages)',
    metadata: {
      source: 'onboarding_flow',
      file_size: 1024000,
      pages: 2,
      status: 'processed'
    }
  },
  {
    id: '4',
    type: 'strategy_updated',
    timestamp: '2024-01-15T09:30:00Z',
    user_id: '1',
    resource_type: 'strategy',
    resource_id: 'objective-1',
    resource_title: 'Job Search Objective',
    details: 'Updated job search strategy to HIGH_COMP with 25% exploration rate',
    metadata: {
      objective: 'HIGH_COMP',
      exploration_rate: 0.25,
      constraints_updated: ['locations', 'salary_floor']
    }
  },
  {
    id: '5',
    type: 'job_viewed',
    timestamp: '2024-01-14T16:20:00Z',
    user_id: '1',
    resource_type: 'job',
    resource_id: 'job-125',
    resource_title: 'Platform Engineer',
    details: 'Viewed Platform Engineer position at CloudTech for 45 seconds',
    metadata: {
      strategy: 'SMALL_CO',
      source: 'search_results',
      company: 'CloudTech',
      location: 'Austin, TX',
      view_duration: 45
    }
  }
];

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const url = new URL(request.url);
  
  const page = parseInt(url.searchParams.get('page') || '1', 10);
  const type = url.searchParams.get('type') || '';
  const resource = url.searchParams.get('resource') || '';
  const dateFrom = url.searchParams.get('date_from') || '';
  const dateTo = url.searchParams.get('date_to') || '';

  try {
    // In a real implementation, this would fetch from the activity API
    // const response = await activityApi.getEvents({ page, type, resource, dateFrom, dateTo });
    
    // Mock implementation
    let filteredEvents = mockEvents;
    
    if (type) {
      filteredEvents = filteredEvents.filter(event => event.type === type);
    }
    
    if (resource) {
      filteredEvents = filteredEvents.filter(event => event.resource_type === resource);
    }

    return {
      user,
      events: filteredEvents,
      total_count: filteredEvents.length,
      page,
      has_more: false,
      filters: { type, resource, date_from: dateFrom, date_to: dateTo },
    };
  } catch (error) {
    return {
      user,
      events: [],
      total_count: 0,
      page: 1,
      has_more: false,
      filters: {},
      error: 'Failed to load activity events',
    };
  }
}

export default function ActivityPage() {
  const { events, total_count, filters } = useLoaderData() as ActivityData;
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);


  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffHours * 60);
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'job_applied': return Send;
      case 'job_saved': return Bookmark;
      case 'job_viewed': return Eye;
      case 'job_rejected': return ThumbsDown;
      case 'resume_uploaded': return Upload;
      case 'resume_updated': return FileText;
      case 'strategy_updated': return Target;
      case 'settings_updated': return Settings;
      default: return Activity;
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'job_applied': return 'text-positive';
      case 'job_saved': return 'text-info';
      case 'job_viewed': return 'text-muted-foreground';
      case 'job_rejected': return 'text-caution';
      case 'resume_uploaded': return 'text-primary';
      case 'resume_updated': return 'text-primary';
      case 'strategy_updated': return 'text-info';
      case 'settings_updated': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getEventDescription = (event: ActivityEvent) => {
    switch (event.type) {
      case 'job_applied':
        return `Applied to ${event.resource_title} at ${event.metadata?.company}`;
      case 'job_saved':
        return `Saved ${event.resource_title} at ${event.metadata?.company}`;
      case 'job_viewed':
        return `Viewed ${event.resource_title} at ${event.metadata?.company}`;
      case 'job_rejected':
        return `Rejected ${event.resource_title} at ${event.metadata?.company}`;
      case 'resume_uploaded':
        return `Uploaded resume: ${event.resource_title}`;
      case 'resume_updated':
        return `Updated resume: ${event.resource_title}`;
      case 'strategy_updated':
        return `Updated job search strategy to ${event.metadata?.objective}`;
      case 'settings_updated':
        return `Updated account settings`;
      default:
        return event.resource_title || 'Unknown activity';
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1'); // Reset pagination
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const eventTypes = [
    { value: '', label: 'All Activities' },
    { value: 'job_applied', label: 'Applications' },
    { value: 'job_saved', label: 'Saved Jobs' },
    { value: 'job_viewed', label: 'Job Views' },
    { value: 'resume_uploaded', label: 'Resume Uploads' },
    { value: 'strategy_updated', label: 'Strategy Updates' },
  ];

  const resourceTypes = [
    { value: '', label: 'All Resources' },
    { value: 'job', label: 'Jobs' },
    { value: 'resume', label: 'Resumes' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'application', label: 'Applications' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Activity</h1>
        </div>
        <p className="text-muted-foreground">
          Chronological feed of your job search activities and system events.
        </p>
      </div>

      {/* Filters Bar */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              <strong className="text-foreground">{total_count}</strong> events
              {Object.values(filters).some(Boolean) && (
                <span className="ml-2">• Filtered</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              className="gap-1"
            >
              <Filter className="h-4 w-4" />
              Filters
            </Button>
            
            {Object.values(filters).some(Boolean) && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearFilters}
                className="gap-1"
              >
                <RefreshCw className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Filter Controls */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Activity Type
                </label>
                <select
                  value={filters.type || ''}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 hover:border-border-hover transition-colors"
                >
                  {eventTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Resource Type
                </label>
                <select
                  value={filters.resource || ''}
                  onChange={(e) => handleFilterChange('resource', e.target.value)}
                  className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 hover:border-border-hover transition-colors"
                >
                  {resourceTypes.map((resource) => (
                    <option key={resource.value} value={resource.value}>
                      {resource.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Date Range
                </label>
                <input
                  type="date"
                  value={filters.date_from || ''}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 hover:border-border-hover transition-colors"
                />
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Activity Feed */}
      {events.length === 0 ? (
        <Card>
          <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
            <Activity className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No activity found</h3>
            <p className="text-muted-foreground mb-4">
              {Object.values(filters).some(Boolean) 
                ? 'No events match your current filters.'
                : 'Start using the platform to see your activity here.'}
            </p>
            <Button 
              onClick={Object.values(filters).some(Boolean) ? clearFilters : () => window.location.href = '/discover'}
              className="gap-2"
            >
              {Object.values(filters).some(Boolean) ? 'Clear Filters' : 'Start Exploring Jobs'}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map((event) => {
            const IconComponent = getEventIcon(event.type);
            const iconColor = getEventColor(event.type);
            
            return (
              <Card key={event.id} className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg bg-muted/30 ${iconColor}`}>
                    <IconComponent className="h-5 w-5" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-foreground">
                          {getEventDescription(event)}
                        </p>
                        
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatTime(event.timestamp)}
                          </span>
                          
                          {event.metadata?.strategy && (
                            <Badge variant="neutral" className="text-xs">
                              {event.metadata.strategy}
                            </Badge>
                          )}
                          
                          {event.metadata?.source && (
                            <span>via {event.metadata.source.replace('_', ' ')}</span>
                          )}
                        </div>
                        
                        {/* Additional details */}
                        {event.metadata && (
                          <div className="mt-2 text-sm text-muted-foreground">
                            {event.type.startsWith('job_') && event.metadata.location && (
                              <span>{event.metadata.location}</span>
                            )}
                            {event.type === 'resume_uploaded' && event.metadata.file_size && (
                              <span>{Math.round(event.metadata.file_size / 1024)} KB • {event.metadata.pages} pages</span>
                            )}
                            {event.type === 'strategy_updated' && event.metadata.exploration_rate && (
                              <span>Exploration: {Math.round(event.metadata.exploration_rate * 100)}%</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex items-center gap-2">
                        {event.resource_type === 'job' && (
                          <Link
                            to={`/jobs/${event.resource_id}`}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        )}
                        {event.resource_type === 'resume' && (
                          <Link
                            to={`/resumes/${event.resource_id}`}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        )}
                        {event.resource_type === 'strategy' && (
                          <Link
                            to="/strategy"
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}